/**
 * When may the Studio home surface take the composer off the screen?
 *
 * ONE rule, and this file is the only place it is written:
 *
 *   **The home composer steps aside only while it is AT REST — holding no
 *   unsent text, running nothing, and showing nothing of its own.**
 *
 * The rule `showHomeComposer` used to carry — "hide as soon as the chat dock
 * opens, unless `composerAlertActive`" — inverted that. `handleSubmit` writes
 * a pending chat notice on its first line, the dock auto-discloses on that
 * notice, and nothing ever closes it again; so from the first submit onward
 * everything the composer rendered lived inside a `hidden` container unless it
 * happened to be one of the two states someone had remembered to except.
 * Measured 2026-08-13: `hero-conversion-without-original`,
 * `hero-landing-handoff`, `hero-chat-planning-progress` and
 * `hero-chat-planning-evidence` all had `getClientRects().length === 0` on
 * 100% of samples, while `hero-chat-planning-error` — the one state the
 * 2026-08-06 fix had enumerated — measured 1.
 *
 * So the surface list here is deliberately NOT a judgement about which
 * notices are important enough to keep the composer alive. Growing such a
 * list is the bug: it is how the failure path got fixed and the success path
 * stayed invisible. The rule is negative — *anything* on screen keeps the
 * composer, and the only question a new surface has to answer is "am I
 * something the composer shows?", not "am I important?".
 */

/** Everything the composer holds that a hide would take away from the user. */
export interface ComposerRestInput {
  /**
   * The exact text in the composer's textarea. Non-empty means the creator
   * has something typed, pasted or carried in that they have not seen
   * consumed yet — hiding it strands their document in a `display:none`
   * element (measured: a 2,128-byte paste, after a button labelled
   * "Not yet — let me edit").
   */
  readonly unsentText: string
  /** A generation this composer started and is still waiting on. */
  readonly running: boolean
  /**
   * The gating value of every surface the composer renders on top of its
   * resting chrome, in any order. `null` / `undefined` / `false` / `""` /
   * `[]` all mean "not on screen"; anything else means the user is looking
   * at it (or would be, if the composer were not hidden).
   */
  readonly surfaces: readonly unknown[]
}

/**
 * Is this surface's gating value currently putting something on screen?
 *
 * Accepts the shapes the composer's state actually uses — a nullable object
 * (the intake cards, the planning evidence), a nullable string (the landing
 * handoff prompt, the planning message), a boolean flag (the
 * conversion-without-original notice) and an array (the clarification
 * questions) — so a new surface never needs this predicate widened just to
 * be counted.
 */
export function composerSurfaceIsOnScreen(surface: unknown): boolean {
  if (surface === null || surface === undefined || surface === false) return false
  if (typeof surface === 'string') return surface.length > 0
  if (Array.isArray(surface)) return surface.length > 0
  return true
}

/** The gating values that are currently putting something on screen. */
export function shownComposerSurfaces(
  surfaces: readonly unknown[],
): readonly unknown[] {
  return surfaces.filter(composerSurfaceIsOnScreen)
}

/**
 * The rule. `true` means the composer is holding nothing the creator still
 * needs, so the home surface may hand the screen to the chat dock (or to an
 * open project). `false` means hiding it would take something away.
 */
export function isComposerAtRest(input: ComposerRestInput): boolean {
  if (input.unsentText.trim().length > 0) return false
  if (input.running) return false
  return shownComposerSurfaces(input.surfaces).length === 0
}
